import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import { useAppSelector, useAppDispatch } from 'src/store';
import { useUpdateIntegrationMutation } from 'src/services';
import { IncludeSubjectField } from 'src/components/Integrations/IntegrationForms/IntegrationFormFields/IncludeSubjectField/IncludeSubjectField';
import { AIAssistantModeFiled } from 'src/components/Integrations/IntegrationForms/IntegrationFormFields/AIAssistantModeField/AIAssistantModeFiled';
import { Separator } from 'src/components/ui/separator';
import { useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { booleanScheme } from 'src/utils/validation-schemes';
import { setSelectedIntegration } from 'src/store/slices/integrationSlice';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

// The reply settings step of the Zendesk and Freshdesk email setups (reply as note, subject).
export const EmailReplySettingsStep = ({
  formId,
  onClose = () => {},
}: {
  formId: string;
  onClose?: (isFinal?: boolean) => void;
}) => {
  const { id } = useParams();
  const notification = useNotification();
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const [
    updateIntegration,
    { data: updatedIntegrationData, isLoading: isLoadingUpdateIntegration },
  ] = useUpdateIntegrationMutation();
  const dispatch = useAppDispatch();

  const { handleSubmit, control } = useForm({
    defaultValues: {
      reply_to_note: selectedIntegration?.integration_data?.reply_to_note || false,
      include_subject_first_response:
        selectedIntegration?.integration_data?.include_subject_first_response || false,
    },
    resolver: formResolver(
      object({
        reply_to_note: booleanScheme().label('AI assistant mode'),
        include_subject_first_response: booleanScheme().label(
          'Include the email subject line in the context',
        ),
      }),
    ),
  });

  useLoaderWhile(isLoadingUpdateIntegration);

  useEffect(() => {
    if (updatedIntegrationData) {
      dispatch(setSelectedIntegration({ ...selectedIntegration, ...updatedIntegrationData }));
    }
  }, [updatedIntegrationData]);

  const submit = async (data: {
    reply_to_note: boolean;
    include_subject_first_response: boolean;
  }) => {
    const request = {
      status: selectedIntegration.status,
      integration_type: selectedIntegration.integration_type,
      integration_data: {
        ...selectedIntegration.integration_data,
        reply_to_note: data.reply_to_note,
        include_subject_first_response: data.include_subject_first_response,
      },
    };

    updateIntegration({
      projectId: id,
      integrationId: selectedIntegration.id,
      body: request,
    }).then((updateIntegrationResult) => {
      if (!('error' in updateIntegrationResult)) {
        notification.success('Integration Updated.');
        onClose(true);
      }
    });
  };

  return (
    <form id={formId} onSubmit={handleSubmit(submit)}>
      <IncludeSubjectField control={control} />
      <Separator className={'bg-[#E3E6E8] my-5'} />
      <AIAssistantModeFiled control={control} />
    </form>
  );
};
